import React, { useState, useEffect } from 'react';
import { View, Text, Image, TouchableOpacity, StyleSheet, ScrollView, Modal, Pressable, TextInput } from 'react-native';
import { useRoute, useNavigation, useFocusEffect, RouteProp, NavigationProp } from '@react-navigation/native';
import { useCallback } from 'react';
import Svg, { Circle } from 'react-native-svg';
import { Ionicons } from '@expo/vector-icons';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useResponsive } from '../../hooks/useResponsive';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import { PageContainer, Navbar, Breadcrumb, ActivityCalendar, Toast } from '../../components';
import TalentsSkeleton from './components/TalentsSkeleton';
import OutputsSection from './components/OutputsSection';
import api from '../../services/api';
import { talentStudentsApi, talentActivitiesApi, talentSpecializationsApi, talentProjectsApi, talentStudentProjectLinksApi, talentStudentSpecializationLinksApi } from '../../services/apiService';
import { API_BASE_HOST } from '../../constants/config';
import { CalendarEvent } from '../../components/ActivityCalendar/types';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { FONTS } from '../../constants/config';
import { createShadow } from '../../utils/styles';
import { getStatusColor } from '../../components/charts/chartStyles';
import {
  StudentDetailData,
  StudentSpecialization,
  SpecializationKPI,
  SpecializationProgram,
  SpecializationOutput,
  TalentActivityData,
} from './types';

const FOREGROUND = '#414042';

type ScreenRouteProp = RouteProp<MainStackParamList, 'StudentDetail'>;
type ScreenNavProp = NavigationProp<MainStackParamList>;
type ProfileRouteProp = RouteProp<MainStackParamList, 'StudentProfile'>;

/* ── Student Header ─────────────────────────────────────────── */
function StudentHeader({ data, onEdit, onRemove, onPhotoChange, onPhotoRemove, readOnly, entityName, isCoordinator, isOrgEmployee, onAccept, onReject, onApprove, isStudentActive, onDeactivate, onActivate }: { data: StudentDetailData; onEdit: () => void; onRemove: () => void; onPhotoChange: () => void; onPhotoRemove?: () => void; readOnly?: boolean; entityName?: string; isCoordinator?: boolean; isOrgEmployee?: boolean; onAccept?: () => void; onReject?: () => void; onApprove?: () => void; isStudentActive?: boolean; onDeactivate?: () => void; onActivate?: () => void }) {
  const { language, t } = useLanguage();
  const isRTL = language === 'ar';
  const studentActive = isStudentActive !== false;
  // Check per-specialization link statuses for approve/reject eligibility
  const hasAppliedLink = data.specializations?.some((s: any) => s.linkStatus === 'applied');
  const hasProposedLink = data.specializations?.some((s: any) => s.linkStatus === 'proposed');
  const canCoordinatorAcceptReject = isCoordinator && studentActive && hasAppliedLink;
  const canCoordinatorEdit = isCoordinator && studentActive && (hasAppliedLink || hasProposedLink);
  const canOrgEmployeeApprove = isOrgEmployee && studentActive && hasProposedLink;
  /* Every application still sitting at 'applied' means the coordinator has not
     reviewed this student yet — staff may not approve, reject or edit them. */
  const awaitingCoordinator = !!data.specializations?.length
    && data.specializations.every((s: any) => s.linkStatus === 'applied');
  const staffLocked = isOrgEmployee && awaitingCoordinator;

  return (
    <View style={[headerStyles.container, createShadow(0, 1, 3, '#000', 0.1)]}>
      <View style={headerStyles.row}>
        {/* Coordinator: Accept/Reject for applied students */}
        {canCoordinatorAcceptReject && (
          <View style={headerStyles.actionsRow}>
            <TouchableOpacity
              style={[headerStyles.actionBtn, { backgroundColor: '#A9211F' }, createShadow(0, 1, 3, '#000', 0.1)]}
              onPress={onAccept}
            >
              <Text style={[headerStyles.actionText, { color: '#FFFFFF' }]}>
                {isRTL ? 'قبول' : 'Accept'}
              </Text>
              <Ionicons name="checkmark" size={15} color="#FFFFFF" />
            </TouchableOpacity>
            <TouchableOpacity
              style={[headerStyles.actionBtn, { borderWidth: 1, borderColor: '#CBD5E1' }, createShadow(0, 1, 3, '#000', 0.1)]}
              onPress={onReject}
            >
              <Text style={headerStyles.actionText}>
                {isRTL ? 'رفض' : 'Reject'}
              </Text>
              <Ionicons name="close" size={15} color={FOREGROUND} />
            </TouchableOpacity>
          </View>
        )}
        {/* Coordinator: Edit/Remove for proposed students */}
        {canCoordinatorEdit && !canCoordinatorAcceptReject && (
          <View style={headerStyles.actionsRow}>
            <TouchableOpacity
              style={[headerStyles.actionBtn, createShadow(0, 1, 3, '#000', 0.1)]}
              onPress={onEdit}
            >
              <Text style={headerStyles.actionText}>
                {isRTL ? 'تعديل البيانات' : 'Edit Data'}
              </Text>
              <Ionicons name="create-outline" size={13} color={FOREGROUND} />
            </TouchableOpacity>
            <TouchableOpacity
              style={[headerStyles.actionBtn, createShadow(0, 1, 3, '#000', 0.1)]}
              onPress={onRemove}
            >
              <Text style={headerStyles.actionText}>
                {isRTL ? 'إلغاء التسجيل' : 'Cancel Registration'}
              </Text>
              <Ionicons name="person-remove-outline" size={13} color={FOREGROUND} />
            </TouchableOpacity>
          </View>
        )}
        {/* Org-employee: Approve/Reject for proposed students (coordinator-approved) */}
        {canOrgEmployeeApprove && (
          <View style={headerStyles.actionsRow}>
            <TouchableOpacity
              style={[headerStyles.actionBtn, { backgroundColor: '#16A34A' }, createShadow(0, 1, 3, '#000', 0.1)]}
              onPress={onApprove}
            >
              <Text style={[headerStyles.actionText, { color: '#FFFFFF' }]}>
                {isRTL ? 'اعتماد' : 'Approve'}
              </Text>
              <Ionicons name="checkmark-circle" size={15} color="#FFFFFF" />
            </TouchableOpacity>
            <TouchableOpacity
              style={[headerStyles.actionBtn, { borderWidth: 1, borderColor: '#CBD5E1' }, createShadow(0, 1, 3, '#000', 0.1)]}
              onPress={onReject}
            >
              <Text style={headerStyles.actionText}>
                {isRTL ? 'رفض' : 'Reject'}
              </Text>
              <Ionicons name="close" size={15} color={FOREGROUND} />
            </TouchableOpacity>
          </View>
        )}
        {/* Staff waiting on the coordinator: no actions, just the reason */}
        {staffLocked && (
          <View style={headerStyles.actionsRow}>
            <Text style={[headerStyles.actionText, { color: '#B45309' }]}>
              {isRTL ? 'بانتظار اعتماد المنسق' : 'Awaiting coordinator approval'}
            </Text>
          </View>
        )}
        {/* Non-coordinator, non-approve: standard edit/deactivate/activate */}
        {!readOnly && !isCoordinator && !canOrgEmployeeApprove && !staffLocked && (
          <View style={headerStyles.actionsRow}>
            {studentActive && (
              <TouchableOpacity
                style={[headerStyles.actionBtn, createShadow(0, 1, 3, '#000', 0.1)]}
                onPress={onEdit}
              >
                <Text style={headerStyles.actionText}>
                  {isRTL ? 'تعديل البيانات' : 'Edit Data'}
                </Text>
                <Ionicons name="create-outline" size={13} color={FOREGROUND} />
              </TouchableOpacity>
            )}
            {studentActive ? (
              <TouchableOpacity
                style={[headerStyles.actionBtn, createShadow(0, 1, 3, '#000', 0.1)]}
                onPress={onDeactivate}
              >
                <Text style={[headerStyles.actionText, { color: '#DC2626' }]}>
                  {t('talentsPage.deactivateStudent')}
                </Text>
                <Ionicons name="person-remove-outline" size={13} color="#DC2626" />
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                style={[headerStyles.actionBtn, { backgroundColor: '#16A34A' }, createShadow(0, 1, 3, '#000', 0.1)]}
                onPress={onActivate}
              >
                <Text style={[headerStyles.actionText, { color: '#FFFFFF' }]}>
                  {t('talentsPage.activateStudent')}
                </Text>
                <Ionicons name="checkmark-circle" size={13} color="#FFFFFF" />
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* Right: Name + Avatar */}
        <View style={headerStyles.identityRow}>
          <View style={headerStyles.nameCol}>
            <Text style={headerStyles.name}>{data.name}</Text>
            <Text style={headerStyles.subtitle}>
              {data.school} - {data.specialization}
            </Text>
            {entityName && (
              <View style={headerStyles.entityTag}>
                <Text style={headerStyles.entityTagText}>{entityName}</Text>
              </View>
            )}
          </View>
          <View style={headerStyles.avatarWrap}>
            {readOnly || !studentActive ? (
              <>
                {data.avatarUrl ? (
                  <Image source={{ uri: data.avatarUrl }} style={headerStyles.avatar} />
                ) : (
                  <View style={[headerStyles.avatar, headerStyles.avatarPlaceholder]}>
                    <Ionicons name="person" size={28} color="#94A3B8" />
                  </View>
                )}
              </>
            ) : (
              <TouchableOpacity onPress={onPhotoChange}>
                {data.avatarUrl ? (
                  <Image source={{ uri: data.avatarUrl }} style={headerStyles.avatar} />
                ) : (
                  <View style={[headerStyles.avatar, headerStyles.avatarPlaceholder]}>
                    <Ionicons name="person" size={28} color="#94A3B8" />
                  </View>
                )}
                <View style={headerStyles.cameraIcon}>
                  <Ionicons name="camera" size={12} color="#FFFFFF" />
                </View>
              </TouchableOpacity>
            )}
            {!readOnly && studentActive && data.avatarUrl && onPhotoRemove && (
              <TouchableOpacity style={headerStyles.removePhotoIcon} onPress={onPhotoRemove}>
                <Ionicons name="close" size={10} color="#FFFFFF" />
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </View>
  );
}

/* ── Info Card Row ──────────────────────────────────────────── */
function InfoRow({ label, value }: { label: string; value: string | React.ReactNode }) {
  return (
    <View style={infoStyles.row}>
      <View style={infoStyles.valueCol}>
        {typeof value === 'string' ? (
          <Text style={infoStyles.valueText}>{value}</Text>
        ) : (
          value
        )}
      </View>
      <View style={infoStyles.labelCol}>
        <Text style={infoStyles.labelText}>{label}</Text>
      </View>
    </View>
  );
}

const STATUS_COLORS: Record<string, string> = {
  active: '#2ca02c',
  proposed: '#1f77b4',
  applied: '#9467bd',
  rejected: '#DC2626',
  withdrawn: '#E97A1F',
  inactive: '#94A3B8',
};

const STATUS_LABELS_AR: Record<string, string> = {
  active: 'معتمد',
  proposed: 'مقترح',
  applied: 'متقدم بطلب',
  rejected: 'مرفوض',
  withdrawn: 'منسحب',
  inactive: 'غير فعال',
};

const STATUS_LABELS_EN: Record<string, string> = {
  active: 'Approved',
  proposed: 'Proposed',
  applied: 'Applied',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
  inactive: 'Inactive',
};

function StatusBadge({ status }: { status: string }) {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const label = isRTL ? (STATUS_LABELS_AR[status] || status) : (STATUS_LABELS_EN[status] || status);
  const bgColor = STATUS_COLORS[status] || '#22C55E';
  return (
    <View style={[infoStyles.badge, { backgroundColor: bgColor }]}>
      <Text style={infoStyles.badgeText}>{label}</Text>
    </View>
  );
}

function InfoCards({ data, isStudentActive }: { data: StudentDetailData; isStudentActive?: boolean }) {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const displayStatus = isStudentActive === false ? 'inactive' : data.status;

  return (
    <View style={infoStyles.cardsRow}>
      {/* Left: Profile Info (الملف التعريفي) */}
      <View style={[infoStyles.card, createShadow(0, 1, 2, '#000', 0.1)]}>
        <Text style={infoStyles.cardTitle}>
          {isRTL ? 'الملف التعريفي' : 'Profile'}
        </Text>
        <InfoRow label={isRTL ? 'المدرسة' : 'School'} value={data.school} />
        <InfoRow label={isRTL ? 'المواهب' : 'Specializations'} value={data.specialization} />
        <InfoRow label={isRTL ? 'الفئة العمرية' : 'Age Group'} value={
          isRTL ? ({ foundation: 'المرحلة التأسيسية', intermediate: 'المرحلة المتوسطة', secondary: 'المرحلة الثانوية' } as any)[data.ageGroup] || data.ageGroup
                : data.ageGroup
        } />
        <InfoRow label={isRTL ? 'المستوى' : 'Level'} value={
          isRTL ? ({ emerging: 'الفئة الناشئة', promising: 'الفئة الواعدة', distinguished: 'الفئة المتميزة', representative: 'الفئة القابلة للتمثيل الخارجي' } as any)[data.level] || data.level
                : data.level
        } />
        <InfoRow
          label={isRTL ? 'الحالة' : 'Status'}
          value={<StatusBadge status={displayStatus} />}
        />
      </View>

      {/* Right: Personal Info (المعلومات الشخصية) */}
      <View style={[infoStyles.card, createShadow(0, 1, 2, '#000', 0.1)]}>
        <Text style={infoStyles.cardTitle}>
          {isRTL ? 'المعلومات الشخصية' : 'Personal Information'}
        </Text>
        <InfoRow label={isRTL ? 'الاسم' : 'Name'} value={data.name} />
        <InfoRow label={isRTL ? 'رقم الهوية' : 'Emirates ID'} value={data.eid ? data.eid.replace(/(\d{3})(\d{4})(\d{7})(\d)/, '$1-$2-$3-$4') : '-'} />
        <InfoRow label={isRTL ? 'البريد الالكتروني' : 'Email'} value={data.email} />
        <InfoRow label={isRTL ? 'الهاتف' : 'Phone'} value={data.phone} />
        <InfoRow label={isRTL ? 'تاريخ الميلاد' : 'Date of Birth'} value={data.birthDate} />
        <InfoRow label={isRTL ? 'الجنس' : 'Gender'} value={data.gender === 'male' ? (isRTL ? 'ذكر' : 'Male') : data.gender === 'female' ? (isRTL ? 'أنثى' : 'Female') : data.gender} />
        <InfoRow label={isRTL ? 'الجنسية' : 'Nationality'} value={data.nationality || '-'} />
        <InfoRow label={isRTL ? 'تاريخ التسجيل' : 'Registration Date'} value={data.registrationDate} />
      </View>
    </View>
  );
}

/* ── Performance Donut (thick, for stats card) ───────────────── */
function PerformanceDonut({ percentage }: { percentage: number }) {
  const size = 140;
  const strokeWidth = 32;
  const center = size / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const progressColor = getStatusColor(percentage);

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size} style={{ position: 'absolute' }}>
        <Circle
          cx={center} cy={center} r={radius}
          stroke="#E5E7EB" strokeWidth={strokeWidth} fill="none"
        />
        <Circle
          cx={center} cy={center} r={radius}
          stroke={progressColor} strokeWidth={strokeWidth} fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          rotation={-90}
          origin={`${center}, ${center}`}
        />
      </Svg>
      <Text style={perfStyles.pctText}>% {percentage}</Text>
    </View>
  );
}

/* ── Performance + KPIs Section ──────────────────────────────── */
function KPIProgressCard({ kpi, onView }: { kpi: SpecializationKPI; onView?: () => void }) {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const progress = kpi.targetValue > 0
    ? Math.round((kpi.achievedValue / kpi.targetValue) * 100)
    : 0;
  const achievedColor = progress >= 50 ? '#2ca02c' : '#ff7f0e';

  return (
    <View style={kpiCardStyles.container}>
      {/* KPI Name */}
      <View style={{ flex: 1 }}>
        <Text style={kpiCardStyles.title}>{kpi.name}</Text>
      </View>
      {/* Metrics row: المحقق + المستهدف */}
      <View style={kpiCardStyles.metricsRow}>
        <View style={kpiCardStyles.metricBox}>
          <Text style={kpiCardStyles.metricLabel}>{isRTL ? 'المحقق' : 'Achieved'}</Text>
          <View style={kpiCardStyles.metricValueRow}>
            <Text style={[kpiCardStyles.metricUnit, { color: achievedColor }]}>%</Text>
            <Text style={[kpiCardStyles.metricValue, { color: achievedColor }]}>{kpi.achievedValue}</Text>
          </View>
        </View>
        <View style={kpiCardStyles.metricBox}>
          <Text style={kpiCardStyles.metricLabel}>{isRTL ? 'المستهدف' : 'Target'}</Text>
          <View style={kpiCardStyles.metricValueRow}>
            <Text style={kpiCardStyles.metricUnit}>%</Text>
            <Text style={kpiCardStyles.metricValue}>{kpi.targetValue}</Text>
          </View>
        </View>
      </View>
      {/* View Details link */}
      <TouchableOpacity style={kpiCardStyles.linkRow} onPress={onView}>
        <Ionicons name="chevron-back" size={14} color={FOREGROUND} />
        <Text style={kpiCardStyles.linkText}>{isRTL ? 'عرض التفاصيل' : 'View Details'}</Text>
      </TouchableOpacity>
    </View>
  );
}

function PerformanceSection({ data, readOnly, disabled, onViewKPI }: { data: StudentDetailData; readOnly?: boolean; disabled?: boolean; onViewKPI?: (kpiId: number) => void }) {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const [toastVisible, setToastVisible] = useState(false);

  return (
    <View style={perfStyles.outerRow}>
      {/* Left: Performance Stats */}
      <View style={[perfStyles.statsCard, createShadow(0, 1, 2, '#000', 0.1)]}>
        <Text style={perfStyles.statsTitle}>
          {isRTL ? 'إحصائيات الأداء' : 'Performance Statistics'}
        </Text>
        <View style={perfStyles.kpiCountRow}>
          <Text style={perfStyles.kpiCountValue}>{data.kpisCount}</Text>
          <View style={perfStyles.kpiCountLabelBar}>
            <Text style={perfStyles.kpiCountLabel}>
              {isRTL ? 'عدد المؤشرات' : 'KPIs Count'}
            </Text>
          </View>
        </View>
        <View style={perfStyles.ringRow}>
          <PerformanceDonut percentage={data.overallPerformance} />
          <View style={perfStyles.kpiCountLabelBar}>
            <Text style={perfStyles.kpiCountLabel}>
              {isRTL ? 'المعدل العام للأداء' : 'Overall Performance'}
            </Text>
          </View>
        </View>
      </View>

      {/* Right: KPIs */}
      <View style={[perfStyles.kpisCard, createShadow(0, 1, 2, '#000', 0.1)]}>
        <View style={perfStyles.kpisHeader}>
          {!readOnly && (
            <TouchableOpacity
              style={[perfStyles.addBtn, createShadow(0, 1, 3, '#000', 0.1), disabled && { opacity: 0.4 }]}
              onPress={() => !disabled && setToastVisible(true)}
              disabled={disabled}
            >
              <Text style={perfStyles.addBtnText}>
                {isRTL ? 'إضافة مؤشر' : 'Add KPI'}
              </Text>
              <Ionicons name="add-circle-outline" size={14} color={FOREGROUND} />
            </TouchableOpacity>
          )}
          <Text style={perfStyles.kpisTitle}>
            {isRTL ? 'مؤشرات الأداء' : 'Performance Indicators'}
          </Text>
        </View>
        {data.kpis.length === 0 ? (
          <View style={perfStyles.kpisEmpty}>
            <Text style={perfStyles.kpisEmptyText}>
              {isRTL ? 'لا توجد مؤشرات لهذا الطالب' : 'No indicators for this student'}
            </Text>
          </View>
        ) : (
          <View style={perfStyles.kpiCardsRow}>
            {data.kpis.map((kpi) => (
              <KPIProgressCard key={kpi.id} kpi={kpi} onView={onViewKPI ? () => onViewKPI(kpi.id) : undefined} />
            ))}
            <View style={perfStyles.cardSpacer} />
            <View style={perfStyles.cardSpacer} />
          </View>
        )}
      </View>
      <Toast visible={toastVisible} message={isRTL ? 'قريباً' : 'Coming soon'} onHide={() => setToastVisible(false)} />
    </View>
  );
}

/* ── Specialization Card (with donut) ────────────────────────── */
function SpecializationDonut({ percentage }: { percentage: number }) {
  const size = 64;
  const strokeWidth = 10;
  const center = size / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const progressColor = getStatusColor(percentage);

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size} style={{ position: 'absolute' }}>
        <Circle cx={center} cy={center} r={radius} stroke="#E5E7EB" strokeWidth={strokeWidth} fill="none" />
        <Circle
          cx={center} cy={center} r={radius}
          stroke={progressColor} strokeWidth={strokeWidth} fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          rotation={-90}
          origin={`${center}, ${center}`}
        />
      </Svg>
      <Text style={{ fontSize: 12, fontWeight: '700', color: FOREGROUND }}>%{percentage}</Text>
    </View>
  );
}

function SpecializationCard({ spec, onView, onRemove, readOnly }: { spec: StudentSpecialization; onView?: () => void; onRemove?: () => void; readOnly?: boolean }) {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <View style={specCardStyles.container}>
      {/* Header: donut + name */}
      <View style={specCardStyles.headerRow}>
        <View style={specCardStyles.nameCol}>
          <Text style={specCardStyles.name}>{spec.title}</Text>
        </View>
        <SpecializationDonut percentage={spec.progress} />
      </View>

      {/* Stats row: 3 metrics */}
      <View style={specCardStyles.statsRow}>
        <View style={specCardStyles.statItem}>
          <Text style={specCardStyles.statLabel}>{isRTL ? 'المؤشرات' : 'KPIs'}</Text>
          <Text style={specCardStyles.statValue}>{spec.kpiCount}</Text>
        </View>
        <View style={specCardStyles.statItem}>
          <Text style={specCardStyles.statLabel}>{isRTL ? 'الأنشطة' : 'Activities'}</Text>
          <Text style={specCardStyles.statValue}>{spec.activityCount}</Text>
        </View>
        <View style={specCardStyles.statItem}>
          <Text style={specCardStyles.statLabel}>{isRTL ? 'المشاركين' : 'Participants'}</Text>
          <Text style={specCardStyles.statValue}>{spec.participantCount}</Text>
        </View>
      </View>

      {/* Footer: link + menu */}
      <View style={specCardStyles.footerRow}>
        {!readOnly && (
          <View>
            <TouchableOpacity onPress={() => setMenuOpen(!menuOpen)}>
              <Ionicons name="ellipsis-vertical" size={18} color={FOREGROUND} />
            </TouchableOpacity>
            {menuOpen && (
              <View style={specCardStyles.dropdownMenu}>
                <TouchableOpacity
                  style={specCardStyles.dropdownItem}
                  onPress={() => { setMenuOpen(false); onRemove?.(); }}
                >
                  <Text style={specCardStyles.dropdownItemText}>
                    {isRTL ? 'إزالة الموهبة' : 'Remove Specialization'}
                  </Text>
                  <Ionicons name="close-circle-outline" size={16} color="#DC2626" />
                </TouchableOpacity>
              </View>
            )}
          </View>
        )}
        <View style={{ flex: 1 }} />
        <TouchableOpacity style={specCardStyles.linkRow} onPress={onView}>
          <Ionicons name="chevron-back" size={14} color={FOREGROUND} />
          <Text style={specCardStyles.linkText}>{isRTL ? 'عرض التفاصيل' : 'View Details'}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

function EnrolledSpecializations({ specializations, onRemoveSpecialization, onAssign, readOnly, disabled, entityName, studentId }: { specializations: StudentSpecialization[]; onRemoveSpecialization?: (specId: number) => void; onAssign?: () => void; readOnly?: boolean; disabled?: boolean; entityName?: string; studentId?: number }) {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const navigation = useNavigation<ScreenNavProp>();
  const [confirmSpecId, setConfirmSpecId] = useState<number | null>(null);
  const confirmSpec = specializations.find(s => s.id === confirmSpecId);

  return (
    <View style={[sectionStyles.container, createShadow(0, 1, 2, '#000', 0.1)]}>
      <View style={sectionStyles.header}>
        {!readOnly && (
          <TouchableOpacity
            style={[sectionStyles.addBtn, createShadow(0, 1, 3, '#000', 0.1), disabled && { opacity: 0.4 }]}
            onPress={() => !disabled && onAssign?.()}
            disabled={disabled}
          >
            <Text style={sectionStyles.addBtnText}>
              {isRTL ? 'إلحاق في موهبة' : 'Enroll in Specialization'}
            </Text>
            <Ionicons name="add-circle-outline" size={14} color={FOREGROUND} />
          </TouchableOpacity>
        )}
        <Text style={sectionStyles.sectionTitle}>
          {isRTL ? 'المواهب المسجل الطالب بها' : 'Enrolled Specializations'}
        </Text>
      </View>
      <View style={sectionStyles.cardsRow}>
        {specializations.map((spec) => (
          <SpecializationCard
            key={spec.id}
            spec={spec}
            readOnly={readOnly || disabled}
            onView={() => navigation.navigate('SpecializationDetail', { id: spec.id, name: spec.title, entityName, studentId })}
            onRemove={() => !disabled && setConfirmSpecId(spec.id)}
          />
        ))}
        <View style={sectionStyles.cardSpacer} />
        <View style={sectionStyles.cardSpacer} />
        <View style={sectionStyles.cardSpacer} />
      </View>
      {/* Confirmation Modal */}
      <Modal visible={confirmSpecId !== null} transparent animationType="fade">
        <Pressable style={modalStyles.overlay} onPress={() => setConfirmSpecId(null)}>
          <Pressable style={modalStyles.content} onPress={(e) => e.stopPropagation()}>
            <Ionicons name="warning-outline" size={40} color="#EAB308" style={{ alignSelf: 'center', marginBottom: 12 }} />
            <Text style={modalStyles.title}>
              {isRTL ? 'إزالة الموهبة' : 'Remove Specialization'}
            </Text>
            <Text style={modalStyles.message}>
              {isRTL
                ? `هل أنت متأكد من إزالة الطالب من موهبة "${confirmSpec?.title || ''}"؟\nسيتم إلغاء تسجيل الطالب من هذه الموهبة.`
                : `Are you sure you want to remove the student from "${confirmSpec?.title || ''}" specialization?\nThe student will be unenrolled from this specialization.`}
            </Text>
            <View style={modalStyles.btnRow}>
              <TouchableOpacity
                style={modalStyles.cancelBtn}
                onPress={() => setConfirmSpecId(null)}
              >
                <Text style={modalStyles.cancelBtnText}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={modalStyles.confirmBtn}
                onPress={() => {
                  if (confirmSpecId !== null) {
                    onRemoveSpecialization?.(confirmSpecId);
                  }
                  setConfirmSpecId(null);
                }}
              >
                <Text style={modalStyles.confirmBtnText}>{isRTL ? 'إزالة' : 'Remove'}</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

/* ── Programs Section ────────────────────────────────────────── */
function ProgramCardInline({ program, onRemove, onView, readOnly }: { program: SpecializationProgram; onRemove?: () => void; onView?: () => void; readOnly?: boolean }) {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <View style={progCardStyles.container}>
      <Text style={progCardStyles.title}>{program.name}</Text>
      <View style={progCardStyles.progressRow}>
        <View style={progCardStyles.pctPill}>
          <Text style={progCardStyles.pctText}>{program.progress}</Text>
          <Text style={progCardStyles.pctText}>%</Text>
        </View>
        <View style={progCardStyles.trackBg}>
          <View style={[progCardStyles.trackFill, { width: `${program.progress}%` }]} />
          <View style={[progCardStyles.progressDot, { right: `${program.progress}%` }]} />
        </View>
      </View>
      <View style={specCardStyles.footerRow}>
        {!readOnly && (
          <View>
            <TouchableOpacity onPress={() => setMenuOpen(!menuOpen)}>
              <Ionicons name="ellipsis-vertical" size={18} color={FOREGROUND} />
            </TouchableOpacity>
            {menuOpen && (
              <View style={specCardStyles.dropdownMenu}>
                <TouchableOpacity
                  style={specCardStyles.dropdownItem}
                  onPress={() => { setMenuOpen(false); onRemove?.(); }}
                >
                  <Text style={specCardStyles.dropdownItemText}>
                    {isRTL ? 'إزالة المشروع' : 'Remove Project'}
                  </Text>
                  <Ionicons name="close-circle-outline" size={16} color="#DC2626" />
                </TouchableOpacity>
              </View>
            )}
          </View>
        )}
        <View style={{ flex: 1 }} />
        <TouchableOpacity style={progCardStyles.linkRow} onPress={onView}>
          <Ionicons name="chevron-back" size={14} color={FOREGROUND} />
          <Text style={progCardStyles.linkText}>{isRTL ? 'عرض التفاصيل' : 'View Details'}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

function EnrolledPrograms({ programs, onRemoveProgram, onAssign, readOnly, disabled, entityName, studentId }: { programs: SpecializationProgram[]; onRemoveProgram?: (prog: SpecializationProgram) => void; onAssign?: () => void; readOnly?: boolean; disabled?: boolean; entityName?: string; studentId?: number }) {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const navigation = useNavigation<ScreenNavProp>();
  const [confirmProg, setConfirmProg] = useState<SpecializationProgram | null>(null);

  return (
    <View style={[sectionStyles.container, createShadow(0, 1, 2, '#000', 0.1)]}>
      <View style={sectionStyles.header}>
        {!readOnly && (
          <TouchableOpacity
            style={[sectionStyles.addBtn, createShadow(0, 1, 3, '#000', 0.1), disabled && { opacity: 0.4 }]}
            onPress={() => !disabled && onAssign?.()}
            disabled={disabled}
          >
            <Text style={sectionStyles.addBtnText}>
              {isRTL ? 'إلحاق في مشروع' : 'Enroll in Project'}
            </Text>
            <Ionicons name="add-circle-outline" size={14} color={FOREGROUND} />
          </TouchableOpacity>
        )}
        <Text style={sectionStyles.sectionTitle}>
          {isRTL ? 'البرامج والمشاريع المسجل الطالب بها' : 'Enrolled Programs & Projects'}
        </Text>
      </View>
      <View style={sectionStyles.cardsRow}>
        {programs.map((prog) => (
          <ProgramCardInline
            key={prog.id}
            program={prog}
            readOnly={readOnly || disabled}
            onRemove={() => !disabled && setConfirmProg(prog)}
            onView={() => navigation.navigate('ProjectDetail', { id: prog.id, name: prog.name, entityName, studentId })}
          />
        ))}
        <View style={sectionStyles.cardSpacer} />
        <View style={sectionStyles.cardSpacer} />
        <View style={sectionStyles.cardSpacer} />
      </View>

      <Modal visible={confirmProg !== null} transparent animationType="fade">
        <Pressable style={modalStyles.overlay} onPress={() => setConfirmProg(null)}>
          <Pressable style={modalStyles.content} onPress={(e) => e.stopPropagation()}>
            <Ionicons name="warning-outline" size={40} color="#EAB308" style={{ alignSelf: 'center', marginBottom: 12 }} />
            <Text style={modalStyles.title}>{isRTL ? 'إزالة المشروع' : 'Remove Project'}</Text>
            <Text style={modalStyles.message}>
              {isRTL
                ? `هل أنت متأكد من إزالة الطالب من مشروع "${confirmProg?.name || ''}"؟\nسيتم إلغاء تسجيل الطالب من هذا المشروع وجميع الأنشطة والجلسات المرتبطة به.`
                : `Are you sure you want to remove the student from "${confirmProg?.name || ''}"?\nThe student will be unenrolled from this project and all related activities and sessions.`}
            </Text>
            <View style={modalStyles.btnRow}>
              <TouchableOpacity style={modalStyles.cancelBtn} onPress={() => setConfirmProg(null)}>
                <Text style={modalStyles.cancelBtnText}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={modalStyles.confirmBtn} onPress={() => { if (confirmProg) onRemoveProgram?.(confirmProg); setConfirmProg(null); }}>
                <Text style={modalStyles.confirmBtnText}>{isRTL ? 'إزالة' : 'Remove'}</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

/* ── Activities Section ──────────────────────────────────────── */
function ActivityCardInline({ activity, onRemove, onView, readOnly }: { activity: TalentActivityData; onRemove?: () => void; onView?: () => void; readOnly?: boolean }) {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <View style={progCardStyles.container}>
      <Text style={progCardStyles.title}>{activity.name}</Text>
      <View style={progCardStyles.progressRow}>
        <View style={progCardStyles.pctPill}>
          <Text style={progCardStyles.pctText}>{activity.progress}</Text>
          <Text style={progCardStyles.pctText}>%</Text>
        </View>
        <View style={progCardStyles.trackBg}>
          <View style={[progCardStyles.trackFill, { width: `${activity.progress}%` }]} />
          <View style={[progCardStyles.progressDot, { right: `${activity.progress}%` }]} />
        </View>
      </View>
      <View style={specCardStyles.footerRow}>
        {!readOnly && (
          <View>
            <TouchableOpacity onPress={() => setMenuOpen(!menuOpen)}>
              <Ionicons name="ellipsis-vertical" size={18} color={FOREGROUND} />
            </TouchableOpacity>
            {menuOpen && (
              <View style={specCardStyles.dropdownMenu}>
                <TouchableOpacity
                  style={specCardStyles.dropdownItem}
                  onPress={() => { setMenuOpen(false); onRemove?.(); }}
                >
                  <Text style={specCardStyles.dropdownItemText}>
                    {isRTL ? 'إزالة النشاط' : 'Remove Activity'}
                  </Text>
                  <Ionicons name="close-circle-outline" size={16} color="#DC2626" />
                </TouchableOpacity>
              </View>
            )}
          </View>
        )}
        <View style={{ flex: 1 }} />
        <TouchableOpacity style={progCardStyles.linkRow} onPress={onView}>
          <Ionicons name="chevron-back" size={14} color={FOREGROUND} />
          <Text style={progCardStyles.linkText}>{isRTL ? 'عرض التفاصيل' : 'View Details'}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

function EnrolledActivities({ activities, onRemoveActivity, onAssign, readOnly, disabled, entityName, studentId }: { activities: TalentActivityData[]; onRemoveActivity?: (act: TalentActivityData) => void; onAssign?: () => void; readOnly?: boolean; disabled?: boolean; entityName?: string; studentId?: number }) {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const navigation = useNavigation<ScreenNavProp>();
  const [confirmAct, setConfirmAct] = useState<TalentActivityData | null>(null);

  return (
    <View style={[sectionStyles.container, createShadow(0, 1, 2, '#000', 0.1)]}>
      <View style={sectionStyles.header}>
        {!readOnly && (
          <TouchableOpacity
            style={[sectionStyles.addBtn, createShadow(0, 1, 3, '#000', 0.1), disabled && { opacity: 0.4 }]}
            onPress={() => !disabled && onAssign?.()}
            disabled={disabled}
          >
            <Text style={sectionStyles.addBtnText}>
              {isRTL ? 'إلحاق في نشاط' : 'Enroll in Activity'}
            </Text>
            <Ionicons name="add-circle-outline" size={14} color={FOREGROUND} />
          </TouchableOpacity>
        )}
        <Text style={sectionStyles.sectionTitle}>
          {isRTL ? 'الأنشطة المسجل الطالب بها' : 'Enrolled Activities'}
        </Text>
      </View>
      <View style={sectionStyles.cardsRow}>
        {activities.map((act) => (
          <ActivityCardInline
            key={act.id}
            activity={act}
            readOnly={readOnly || disabled}
            onRemove={() => !disabled && setConfirmAct(act)}
            onView={() => navigation.navigate('TalentActivityDetail', { id: act.id, entityName, studentId })}
          />
        ))}
        <View style={sectionStyles.cardSpacer} />
        <View style={sectionStyles.cardSpacer} />
        <View style={sectionStyles.cardSpacer} />
      </View>

      <Modal visible={confirmAct !== null} transparent animationType="fade">
        <Pressable style={modalStyles.overlay} onPress={() => setConfirmAct(null)}>
          <Pressable style={modalStyles.content} onPress={(e) => e.stopPropagation()}>
            <Ionicons name="warning-outline" size={40} color="#EAB308" style={{ alignSelf: 'center', marginBottom: 12 }} />
            <Text style={modalStyles.title}>{isRTL ? 'إزالة النشاط' : 'Remove Activity'}</Text>
            <Text style={modalStyles.message}>
              {isRTL
                ? `هل أنت متأكد من إزالة النشاط "${confirmAct?.name || ''}"؟\nسيتم حذف النشاط وجميع الجلسات المرتبطة به.`
                : `Are you sure you want to remove "${confirmAct?.name || ''}"?\nThe activity and all related sessions will be deleted.`}
            </Text>
            <View style={modalStyles.btnRow}>
              <TouchableOpacity style={modalStyles.cancelBtn} onPress={() => setConfirmAct(null)}>
                <Text style={modalStyles.cancelBtnText}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={modalStyles.confirmBtn} onPress={() => { if (confirmAct) onRemoveActivity?.(confirmAct); setConfirmAct(null); }}>
                <Text style={modalStyles.confirmBtnText}>{isRTL ? 'إزالة' : 'Remove'}</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}


/* ── Main Screen ─────────────────────────────────────────────── */
export default function StudentDetailScreen() {
  const route = useRoute<any>();
  const navigation = useNavigation<ScreenNavProp>();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const { isMobile } = useResponsive();
  const containerPadding = isMobile ? 12 : 24;

  const { colors } = useTheme();
  const { user } = useAuth();
  const isStrategyTeam = user?.profile?.user_type === 'strategy_team' || user?.profile?.user_type === 'superuser';
  const isCoordinator = user?.profile?.user_type === 'coordinator';
  const isOrgEmployee = user?.profile?.user_type === 'org_employee';
  const { isGuardian } = useRoleAccess();

  // Detect self-profile mode (student viewing their own profile)
  const isMyProfile = route.name === 'StudentProfile';
  const [resolvedStudentId, setResolvedStudentId] = useState<number | undefined>();
  const studentId = isMyProfile ? resolvedStudentId : route.params?.id;
  const entityName = isMyProfile ? undefined : route.params?.entityName;
  // A guardian opens this screen for their own child, so neither isMyProfile nor
  // entityName is set — without isGuardian they were handed the staff toolbar
  // (Add KPI, edit, deactivate, assign, remove), all of which 403 on the server.
  const readOnly = isMyProfile || !!entityName || isStrategyTeam || isCoordinator || isGuardian;
  const [studentBlocked, setStudentBlocked] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [toastMsg, setToastMsg] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('info');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isStudentActive, setIsStudentActive] = useState(true);

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<StudentDetailData>({
    id: 0, name: '', school: '', specialization: '', avatarUrl: undefined,
    eid: '', email: '', phone: '', birthDate: '', gender: '', nationality: '', registrationDate: '',
    ageGroup: '', level: '', status: 'active',
    overallPerformance: 0, kpisCount: 0, kpis: [],
    specializations: [], programs: [], activities: [], outputs: [],
  });
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [assignType, setAssignType] = useState<'specialization' | 'project' | 'activity' | null>(null);
  const [assignOptions, setAssignOptions] = useState<{ id: number; name: string }[]>([]);
  const [assignLoading, setAssignLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    let effectiveStudentId = studentId;
    // Fetch student profile
    try {
      const resp = isMyProfile
        ? await talentStudentsApi.myProfile()
        : await talentStudentsApi.profile(studentId);
      if (isMyProfile && resp?.id) {
        setResolvedStudentId(resp.id);
        effectiveStudentId = resp.id;
      }
      if (resp) {
        const transformed: StudentDetailData = {
          id: resp.id,
          name: resp.name || '',
          school: resp.school || '',
          specialization: resp.specialization_name || resp.specialization || '',
          avatarUrl: resp.photo ? (resp.photo.startsWith('http') ? resp.photo : `${API_BASE_HOST}${resp.photo}`) : undefined,
          eid: resp.eid || '',
          email: resp.email || '',
          phone: resp.phone || '',
          birthDate: resp.birth_date || '',
          gender: resp.gender || '',
          nationality: resp.nationality || '',
          registrationDate: resp.registration_date || '',
          ageGroup: resp.age_group || '',
          level: resp.level || '',
          status: resp.status || 'active',
          overallPerformance: resp.kpis?.length > 0
            ? Math.round(resp.kpis.reduce((sum: number, k: any) => {
                const t = k.target_value || 1;
                return sum + Math.min((k.achieved_value / t) * 100, 100);
              }, 0) / resp.kpis.length)
            : (resp.performance ?? 0),
          kpisCount: resp.kpis?.length ?? 0,
          kpis: (resp.kpis || []).map((k: any) => ({
            id: k.id,
            name: k.kpi_name || k.name || '',
            targetValue: k.target_value ?? 0,
            achievedValue: k.achieved_value ?? 0,
            unit: k.unit || 'percentage',
            isAchieved: (k.achieved_value ?? 0) >= (k.target_value ?? 0),
          })),
          specializations: (resp.specializations || []).map((s: any) => ({
            id: s.id,
            linkId: s.link_id ?? null,
            linkStatus: s.link_status || 'active',
            title: s.title || s.name || '',
            progress: s.progress ?? 0,
            kpiCount: s.kpiCount ?? 0,
            activityCount: s.activityCount ?? 0,
            participantCount: s.participantCount ?? 0,
          })),
          programs: (resp.projects || []).map((p: any) => ({
            id: p.project || p.id,
            name: p.project_name || p.name || '',
            progress: p.progress_percentage ?? p.progress ?? 0,
          })),
          activities: (resp.activities || []).map((a: any) => ({
            id: a.id,
            name: a.name || '',
            progress: a.progress_percentage ?? a.progress ?? 0,
          })),
          outputs: (resp.outputs || []).map((o: any) => ({
            id: o.id,
            title: o.title || '',
            image: (() => { const src = o.thumbnail || o.file; return src ? (src.startsWith('http') ? src : `${API_BASE_HOST}${src}`) : undefined; })(),
          })),
        };
        setData(transformed);
        setIsStudentActive(resp.is_active !== false);
        setStudentBlocked(['withdrawn', 'rejected'].includes(resp.status));
      }
    } catch (e) {
      console.log('[StudentDetail] API failed:', e);
    }
    // Fetch calendar events for student sessions
    if (effectiveStudentId) {
      try {
        const events = await talentActivitiesApi.calendar({ student: effectiveStudentId });
        // Use 'painting' category for teal color, remove participant count
        const sessionEvents = Array.isArray(events)
          ? events.map((e: any) => ({ ...e, category: 'painting', participantCount: undefined }))
          : [];
        setCalendarEvents(sessionEvents);
      } catch (e) {
        console.log('[StudentDetail] Calendar API failed:', e);
        setCalendarEvents([]);
      }
    }
    setIsLoading(false);
  }, [studentId, isMyProfile]);

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData])
  );

  const handleDeleteStudent = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDeactivateStudent = async () => {
    setShowDeleteConfirm(false);
    try {
      await talentStudentsApi.deactivate(studentId);
      setIsStudentActive(false);
      setToastType('success');
      setToastMsg(t('talentsPage.studentDeactivated'));
    } catch (e) {
      console.log('[StudentDetail] deactivate failed:', e);
      setToastType('error');
      setToastMsg(t('talentsPage.deactivateError'));
    }
  };

  const handleActivateStudent = async () => {
    try {
      await talentStudentsApi.activate(studentId);
      setIsStudentActive(true);
      setToastType('success');
      setToastMsg(t('talentsPage.studentActivated'));
    } catch (e) {
      console.log('[StudentDetail] activate failed:', e);
      setToastType('error');
      setToastMsg(t('talentsPage.activateError'));
    }
  };

  const handlePhotoRemove = async () => {
    try {
      await api.patch(
        `/talent-students/${studentId}/`,
        { photo: null },
      );
      setToastType('success');
      setToastMsg(isRTL ? 'تم إزالة الصورة بنجاح' : 'Photo removed successfully');
      fetchData();
    } catch (err) {
      console.log('[StudentDetail] photo remove failed:', err);
      setToastType('error');
      setToastMsg(isRTL ? 'فشل في إزالة الصورة' : 'Failed to remove photo');
    }
  };

  const handlePhotoChange = () => {
    // Web: trigger hidden file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg';
    input.onchange = async (e: any) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const formData = new FormData();
        formData.append('photo', file);
        await api.patch(
          `/talent-students/${studentId}/`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        setToastType('success');
        setToastMsg(isRTL ? 'تم تحديث الصورة بنجاح' : 'Photo updated successfully');
        fetchData();
      } catch (err) {
        console.log('[StudentDetail] photo upload failed:', err);
        setToastType('error');
        setToastMsg(isRTL ? 'فشل في تحديث الصورة' : 'Failed to update photo');
      }
    };
    input.click();
  };

  const openAssignModal = async (type: 'specialization' | 'project' | 'activity') => {
    setAssignType(type);
    setAssignLoading(true);
    setAssignOptions([]);
    try {
      if (type === 'specialization') {
        const resp = await talentSpecializationsApi.list();
        const items = resp?.results || resp || [];
        const enrolledIds = new Set(data.specializations.map(s => s.id));
        setAssignOptions(items.filter((s: any) => !enrolledIds.has(s.id)).map((s: any) => ({ id: s.id, name: s.name })));
      } else if (type === 'project') {
        const resp = await talentProjectsApi.list();
        const items = resp?.results || resp || [];
        const enrolledIds = new Set(data.programs.map(p => p.id));
        setAssignOptions(items.filter((p: any) => !enrolledIds.has(p.id)).map((p: any) => ({ id: p.id, name: p.name })));
      } else {
        const resp = await talentActivitiesApi.list();
        const items = resp?.results || resp || [];
        const enrolledProjectIds = new Set(data.programs.map(p => p.id));
        setAssignOptions(items.filter((a: any) => !enrolledProjectIds.has(a.project)).map((a: any) => ({ id: a.id, name: a.name })));
      }
    } catch (e) {
      console.log('[StudentDetail] fetch assign options failed:', e);
    }
    setAssignLoading(false);
  };

  const handleAssign = async (selectedId: number) => {
    try {
      if (assignType === 'specialization') {
        await talentStudentSpecializationLinksApi.create({ student: studentId, specialization: selectedId });
        setToastType('success');
        setToastMsg(isRTL ? 'تم إلحاق الموهبة بنجاح' : 'Specialization assigned successfully');
      } else if (assignType === 'project') {
        // Check if student already enrolled in this project
        if (data.programs.some(p => p.id === selectedId)) {
          setToastType('error');
          setToastMsg(isRTL ? 'الطالب مسجل بالفعل في هذا المشروع' : 'Student already enrolled in this project');
          return;
        }
        await talentStudentProjectLinksApi.create({ student: studentId, project: selectedId });
        setToastType('success');
        setToastMsg(isRTL ? 'تم إلحاق المشروع بنجاح' : 'Project assigned successfully');
      } else if (assignType === 'activity') {
        const actResp = await talentActivitiesApi.get(selectedId);
        if (actResp?.project) {
          // Check if already enrolled in this project
          if (data.programs.some(p => p.id === actResp.project)) {
            setToastType('error');
            setToastMsg(isRTL ? 'الطالب مسجل بالفعل في هذا النشاط' : 'Student already enrolled in this activity');
            return;
          }
          await talentStudentProjectLinksApi.create({ student: studentId, project: actResp.project });
        }
        setToastType('success');
        setToastMsg(isRTL ? 'تم إلحاق النشاط بنجاح' : 'Activity assigned successfully');
      }
      setAssignType(null);
      fetchData();
    } catch (e: any) {
      console.log('[StudentDetail] assign failed:', e);
      const detail = e?.response?.data?.detail || e?.response?.data?.non_field_errors?.[0] || '';
      if (detail.includes('unique') || detail.includes('already')) {
        setToastType('error');
        setToastMsg(isRTL ? 'الطالب مسجل بالفعل' : 'Student already enrolled');
      } else {
        setToastType('error');
        setToastMsg(isRTL ? 'فشل في الإلحاق' : 'Failed to assign');
      }
      setAssignType(null);
    }
  };

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={[styles.container, { padding: containerPadding }]}>
          <Breadcrumb
            items={isMyProfile ? [
              { label: t('navigation.home') },
              { label: t('navigation.talents'), route: 'Talents' },
              { label: isRTL ? 'ملفي الشخصي' : 'My Profile' },
            ] : [
              { label: t('navigation.home') },
              { label: t('navigation.talents'), route: 'Talents' },
              ...(entityName
                ? [{ label: entityName, route: 'SpecializationDetail', params: { name: entityName } }]
                : []),
              { label: isRTL ? 'تفاصيل الطالب' : 'Student Details' },
            ]}
          />

          {isLoading ? (
            <TalentsSkeleton />
          ) : (
            <View style={styles.sections}>
              {/* Inactive Banner */}
              {!isStudentActive && (
                <View style={{ backgroundColor: '#FEF2F2', borderWidth: 1, borderColor: '#FECACA', borderRadius: 10, padding: 14, flexDirection: isRTL ? 'row-reverse' : 'row', alignItems: 'center', gap: 10 }}>
                  <Ionicons name="alert-circle" size={22} color="#DC2626" />
                  <Text style={{ flex: 1, fontSize: 14, fontFamily: FONTS.regular, color: '#991B1B', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('talentsPage.inactiveBanner')}
                  </Text>
                </View>
              )}

              {/* Section 1: Student Header */}
              <StudentHeader
                data={data}
                onEdit={() => navigation.navigate('EditStudent', { id: studentId })}
                onRemove={handleDeleteStudent}
                onPhotoChange={handlePhotoChange}
                onPhotoRemove={handlePhotoRemove}
                readOnly={readOnly || studentBlocked}
                entityName={entityName}
                isCoordinator={isCoordinator}
                isOrgEmployee={isOrgEmployee}
                isStudentActive={isStudentActive && !studentBlocked}
                onDeactivate={handleDeleteStudent}
                onActivate={handleActivateStudent}
                onAccept={async () => {
                  try {
                    const appliedLinks = (data.specializations || []).filter((s: any) => s.linkStatus === 'applied' && s.linkId);
                    await talentStudentsApi.bulkApproveLinks(appliedLinks.map((s: any) => s.linkId));
                    setToastMsg(isRTL ? 'تم قبول الطالب وإرسال الطلب لموظف الجهة' : 'Student accepted, sent to org-employee for approval');
                    setToastType('success');
                    fetchData();
                  } catch { setToastMsg(isRTL ? 'فشل في قبول الطالب' : 'Failed to accept'); setToastType('error'); }
                }}
                onReject={() => { setRejectReason(''); setShowRejectModal(true); }}
                onApprove={async () => {
                  try {
                    const proposedLinks = (data.specializations || []).filter((s: any) => s.linkStatus === 'proposed' && s.linkId);
                    await talentStudentsApi.bulkActivateLinks(proposedLinks.map((s: any) => s.linkId));
                    setToastMsg(isRTL ? 'تم اعتماد الطالب وتفعيله' : 'Student approved and activated');
                    setToastType('success');
                    fetchData();
                  } catch { setToastMsg(isRTL ? 'فشل في اعتماد الطالب' : 'Failed to approve'); setToastType('error'); }
                }}
              />

              {/* Section 2: Info Cards */}
              <InfoCards data={data} isStudentActive={isStudentActive} />

              {/* Section 3: Performance + KPIs */}
              <PerformanceSection data={data} readOnly={readOnly || studentBlocked} disabled={!isStudentActive || studentBlocked} onViewKPI={(kpiLinkId) => navigation.navigate('TalentKPIDetail' as any, { kpiLinkId })} />

              {/* Section 4: Enrolled Specializations */}
              <EnrolledSpecializations
                studentId={studentId}
                specializations={data.specializations}
                readOnly={readOnly || studentBlocked}
                disabled={!isStudentActive || studentBlocked}
                entityName={entityName}
                onAssign={() => openAssignModal('specialization')}
                onRemoveSpecialization={async (specId) => {
                  try {
                    // Find the link ID for this specialization
                    const spec = data.specializations.find(s => s.id === specId);
                    if (spec?.linkId) {
                      await talentStudentSpecializationLinksApi.delete(spec.linkId);
                    } else {
                      // Fallback: legacy specialization_fk removal
                      await talentStudentsApi.removeSpecialization(studentId);
                    }
                    setToastType('success');
                    setToastMsg(isRTL ? 'تم إزالة الموهبة بنجاح' : 'Specialization removed successfully');
                    fetchData();
                  } catch (e) {
                    console.log('[StudentDetail] remove specialization failed:', e);
                    setToastType('error');
                    setToastMsg(isRTL ? 'فشل في إزالة الموهبة' : 'Failed to remove specialization');
                  }
                }}
              />

              {/* Section 5: Programs & Projects */}
              <EnrolledPrograms
                studentId={studentId}
                programs={data.programs}
                readOnly={readOnly || studentBlocked}
                disabled={!isStudentActive || studentBlocked}
                entityName={entityName}
                onAssign={() => openAssignModal('project')}
                onRemoveProgram={async (prog) => {
                  try {
                    await talentStudentsApi.removeProject(studentId, prog.id);
                    setToastType('success');
                    setToastMsg(isRTL ? 'تم إزالة المشروع بنجاح' : 'Project removed successfully');
                    fetchData();
                  } catch (e) {
                    console.log('[StudentDetail] remove project failed:', e);
                    setToastType('error');
                    setToastMsg(isRTL ? 'فشل في إزالة المشروع' : 'Failed to remove project');
                  }
                }}
              />

              {/* Section 6: Activities */}
              <EnrolledActivities
                studentId={studentId}
                activities={data.activities}
                readOnly={readOnly || studentBlocked}
                disabled={!isStudentActive || studentBlocked}
                entityName={entityName}
                onAssign={() => openAssignModal('activity')}
                onRemoveActivity={async (act) => {
                  try {
                    await talentActivitiesApi.delete(act.id);
                    setToastType('success');
                    setToastMsg(isRTL ? 'تم إزالة النشاط بنجاح' : 'Activity removed successfully');
                    fetchData();
                  } catch (e) {
                    console.log('[StudentDetail] remove activity failed:', e);
                    setToastType('error');
                    setToastMsg(isRTL ? 'فشل في إزالة النشاط' : 'Failed to remove activity');
                  }
                }}
              />

              {/* Section 7: Calendar — shows sessions */}
              <ActivityCalendar
                events={calendarEvents}
                hideNewButton
                titleOverride={isRTL ? 'التقويم' : 'Calendar'}
                onEventPress={(event) => navigation.navigate('TalentActivityDetail', { id: event.id, entityName, studentId })}
              />

              {/* Section 8: Student Outputs */}
              <OutputsSection outputs={data.outputs} projectId={data.programs[0]?.id} titleOverride={isRTL ? 'مخرجات مشاركة الطالب' : 'Student Outputs'} readOnly={readOnly} disabled={!isStudentActive} />
            </View>
          )}
        </View>
      </ScrollView>
      {/* Assign Modal */}
      <Modal visible={assignType !== null} transparent animationType="fade">
        <Pressable style={modalStyles.overlay} onPress={() => setAssignType(null)}>
          <Pressable style={[modalStyles.content, { width: 380 }]} onPress={(e) => e.stopPropagation()}>
            <Text style={modalStyles.title}>
              {assignType === 'specialization' ? (isRTL ? 'إلحاق في موهبة' : 'Assign to Specialization')
                : assignType === 'project' ? (isRTL ? 'إلحاق في مشروع' : 'Assign to Project')
                : (isRTL ? 'إلحاق في نشاط' : 'Assign to Activity')}
            </Text>
            {assignLoading ? (
              <Text style={{ textAlign: 'center', color: '#94A3B8', fontFamily: FONTS.regular, fontSize: 14, paddingVertical: 20 }}>
                {isRTL ? 'جاري التحميل...' : 'Loading...'}
              </Text>
            ) : assignOptions.length === 0 ? (
              <Text style={{ textAlign: 'center', color: '#94A3B8', fontFamily: FONTS.regular, fontSize: 14, paddingVertical: 20 }}>
                {isRTL ? 'لا توجد خيارات متاحة' : 'No options available'}
              </Text>
            ) : (
              <ScrollView style={{ maxHeight: 300 }}>
                {assignOptions.map((opt) => (
                  <TouchableOpacity
                    key={opt.id}
                    style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 10, paddingHorizontal: 8, borderBottomWidth: 1, borderBottomColor: '#f1f5f9' }}
                    onPress={() => handleAssign(opt.id)}
                  >
                    <Ionicons name="add-circle-outline" size={18} color="#22C55E" />
                    <Text style={{ flex: 1, fontSize: 14, fontFamily: FONTS.regular, textAlign: 'right', color: FOREGROUND }}>{opt.name}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            )}
            <View style={{ marginTop: 16 }}>
              <TouchableOpacity style={modalStyles.cancelBtn} onPress={() => setAssignType(null)}>
                <Text style={modalStyles.cancelBtnText}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      <Modal visible={showDeleteConfirm} transparent animationType="fade" onRequestClose={() => setShowDeleteConfirm(false)}>
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }}>
          <View style={{ backgroundColor: colors.card, borderRadius: 12, padding: 24, width: 480, maxWidth: '90%', alignItems: 'center' }}>
            <Ionicons name="warning-outline" size={40} color="#EAB308" style={{ marginBottom: 12 }} />
            <Text style={{ fontSize: 18, fontFamily: FONTS.medium, color: colors.textPrimary, marginBottom: 8 }}>
              {t('talentsPage.deactivateConfirmTitle')}
            </Text>
            <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: colors.textSecondary, textAlign: 'center', lineHeight: 22, marginBottom: 20 }}>
              {t('talentsPage.deactivateConfirmMessage', { name: data.name })}
            </Text>
            <View style={{ flexDirection: 'row', gap: 12, width: '100%' }}>
              <TouchableOpacity style={{ flex: 1, paddingVertical: 12, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0', alignItems: 'center' }} onPress={() => setShowDeleteConfirm(false)}>
                <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#414042' }}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={{ flex: 1, paddingVertical: 12, borderRadius: 8, backgroundColor: '#DC2626', alignItems: 'center' }} onPress={confirmDeactivateStudent}>
                <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' }}>{t('talentsPage.deactivateStudent')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Reject Confirmation Modal */}
      <Modal visible={showRejectModal} transparent animationType="fade" onRequestClose={() => setShowRejectModal(false)}>
        <Pressable style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'center', alignItems: 'center' }} onPress={() => setShowRejectModal(false)}>
          <Pressable style={{ backgroundColor: '#FFFFFF', borderRadius: 16, padding: 24, width: 440, maxWidth: '90%' }} onPress={(e) => e.stopPropagation()}>
            <Ionicons name="warning-outline" size={40} color="#DC2626" style={{ alignSelf: 'center', marginBottom: 12 }} />
            <Text style={{ fontSize: 18, fontFamily: FONTS.medium, color: '#414042', textAlign: 'center', marginBottom: 8 }}>
              {isRTL ? 'تأكيد رفض الطالب' : 'Confirm Student Rejection'}
            </Text>
            <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: '#64748B', textAlign: 'center', lineHeight: 22, marginBottom: 16 }}>
              {isRTL ? `هل أنت متأكد من رفض "${data.name}"؟` : `Are you sure you want to reject "${data.name}"?`}
            </Text>
            <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: '#414042', textAlign: isRTL ? 'right' : 'left', marginBottom: 8 }}>
              {isRTL ? 'سبب الرفض *' : 'Reason for rejection *'}
            </Text>
            <TextInput
              style={{ borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 8, padding: 12, minHeight: 80, fontFamily: FONTS.regular, fontSize: 14, color: '#414042', textAlign: isRTL ? 'right' : 'left', textAlignVertical: 'top', backgroundColor: '#F8FAFC', marginBottom: 20 }}
              placeholder={isRTL ? 'اكتب سبب الرفض...' : 'Enter rejection reason...'}
              placeholderTextColor="#94A3B8"
              value={rejectReason}
              onChangeText={setRejectReason}
              multiline
            />
            <View style={{ flexDirection: 'row', gap: 12, justifyContent: 'center' }}>
              <TouchableOpacity
                style={{ flex: 1, borderWidth: 1, borderColor: '#CBD5E1', borderRadius: 8, paddingVertical: 10, alignItems: 'center' }}
                onPress={() => setShowRejectModal(false)}
              >
                <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#414042' }}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={{ flex: 1, backgroundColor: rejectReason.trim() ? '#DC2626' : '#94A3B8', borderRadius: 8, paddingVertical: 10, alignItems: 'center' }}
                disabled={!rejectReason.trim()}
                onPress={async () => {
                  try {
                    // Staff may only reject what the coordinator already approved
                    const rejectableStatuses = isOrgEmployee ? ['proposed'] : ['applied', 'proposed'];
                    const pendingLinks = (data.specializations || []).filter(
                      (s: any) => rejectableStatuses.includes(s.linkStatus) && s.linkId,
                    );
                    if (pendingLinks.length === 0) {
                      setToastMsg(isRTL ? 'لا يمكن رفض طلب بانتظار اعتماد المنسق' : 'Cannot reject an application awaiting coordinator approval');
                      setToastType('error');
                      return;
                    }
                    await talentStudentsApi.bulkRejectLinks(pendingLinks.map((s: any) => s.linkId), rejectReason.trim());
                    setToastMsg(isRTL ? 'تم رفض الطالب' : 'Student rejected');
                    setToastType('success');
                    setShowRejectModal(false);
                    fetchData();
                  } catch { setToastMsg(isRTL ? 'فشل في رفض الطالب' : 'Failed to reject'); setToastType('error'); }
                }}
              >
                <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' }}>{isRTL ? 'تأكيد الرفض' : 'Confirm Reject'}</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      <Toast visible={!!toastMsg} message={toastMsg} type={toastType} onHide={() => { setToastMsg(''); setToastType('info'); }} />
    </PageContainer>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Styles                                                       */
/* ══════════════════════════════════════════════════════════════ */

const styles = StyleSheet.create({
  scrollView: { flex: 1 },
  scrollContent: { flexGrow: 1 },
  container: { flex: 1 },
  sections: { gap: 20 },
});

/* ── Header ─────────────────────────────────────────────────── */
const headerStyles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 20,
  },
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 20,
  },
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    minHeight: 32,
  },
  actionText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
  },
  identityRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 20,
  },
  nameCol: {
    alignItems: 'flex-end',
    gap: 8,
  },
  name: {
    fontSize: 20,
    fontFamily: FONTS.bold,
    color: FOREGROUND,
    textAlign: 'right',
    lineHeight: 26,
  },
  subtitle: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'right',
    lineHeight: 24,
  },
  avatarWrap: {
    position: 'relative' as const,
  },
  avatar: {
    width: 72,
    height: 72,
    borderRadius: 4,
  },
  avatarPlaceholder: {
    backgroundColor: '#E2E8F0',
    alignItems: 'center',
    justifyContent: 'center',
  },
  removePhotoIcon: {
    position: 'absolute' as const,
    top: -4,
    left: -4,
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: '#DC2626',
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    borderWidth: 2,
    borderColor: '#FFFFFF',
    zIndex: 2,
  },
  cameraIcon: {
    position: 'absolute' as const,
    bottom: -4,
    right: -4,
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: '#A9211F',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: '#FFFFFF',
  },
  entityTag: {
    backgroundColor: '#A6813F',
    borderRadius: 40,
    paddingHorizontal: 12,
    paddingVertical: 4,
    alignSelf: 'flex-end',
  },
  entityTagText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#FFFFFF',
  },
});

/* ── Info Cards ─────────────────────────────────────────────── */
const infoStyles = StyleSheet.create({
  cardsRow: {
    flexDirection: 'row',
    gap: 20,
  },
  card: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderColor: '#e2e8f0',
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    gap: 16,
  },
  cardTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
    textAlign: 'right',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  labelCol: {
    width: 120,
  },
  labelText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'right',
  },
  valueCol: {
    flex: 1,
    alignItems: 'flex-end',
  },
  valueText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
    textAlign: 'right',
  },
  badge: {
    backgroundColor: '#22C55E',
    borderRadius: 4,
    paddingHorizontal: 12,
    paddingVertical: 4,
  },
  badgeText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#FFFFFF',
    textAlign: 'center',
  },
});

/* ── Performance Section ────────────────────────────────────── */
const perfStyles = StyleSheet.create({
  outerRow: {
    flexDirection: 'row',
    gap: 20,
  },
  statsCard: {
    width: 320,
    backgroundColor: '#FFFFFF',
    borderColor: '#e2e8f0',
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    gap: 16,
  },
  statsTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
    textAlign: 'right',
  },
  kpiCountRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
  },
  kpiCountLabelBar: {
    width: 120,
    backgroundColor: 'rgba(51,65,85,0.1)',
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 2,
    alignItems: 'flex-end',
  },
  kpiCountLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
    textAlign: 'right',
  },
  kpiCountValue: {
    fontSize: 12,
    fontFamily: FONTS.numbers,
    color: FOREGROUND,
    textAlign: 'right',
    flex: 1,
  },
  ringRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },
  pctText: {
    fontSize: 20,
    fontWeight: '700',
    textAlign: 'center',
    color: FOREGROUND,
  },
  kpisCard: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderColor: '#e2e8f0',
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    gap: 16,
  },
  kpisHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    width: '100%',
  },
  kpisTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
    textAlign: 'right',
    flex: 1,
    lineHeight: 20,
  },
  addBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    minHeight: 32,
  },
  addBtnText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'center',
    color: FOREGROUND,
  },
  kpiCardsRow: {
    flexDirection: 'row-reverse',
    gap: 20,
    borderRadius: 8,
    padding: 12,
    backgroundColor: '#f1f5f9',
  },
  kpisEmpty: {
    borderRadius: 8,
    padding: 24,
    backgroundColor: '#f1f5f9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  kpisEmptyText: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#94A3B8',
    textAlign: 'center',
  },
  cardSpacer: {
    flex: 1,
  },
});

/* ── KPI Card (progress bar style) ──────────────────────────── */
const kpiCardStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    padding: 12,
    gap: 8,
  },
  title: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'right',
    lineHeight: 20,
    color: FOREGROUND,
  },
  metricsRow: {
    flexDirection: 'row',
    gap: 8,
  },
  metricBox: {
    flex: 1,
    backgroundColor: '#f1f5f9',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
    gap: 4,
    alignItems: 'flex-end',
  },
  metricLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
    textAlign: 'right',
  },
  metricValueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  metricUnit: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
  },
  metricValue: {
    fontSize: 14,
    fontFamily: FONTS.numbers,
    fontWeight: '600',
    color: FOREGROUND,
    textAlign: 'right',
  },
  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    justifyContent: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
    minHeight: 24,
  },
  linkText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    textAlign: 'right',
    lineHeight: 14,
    color: FOREGROUND,
  },
});

/* ── Shared Section Styles ──────────────────────────────────── */
const sectionStyles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderColor: '#e2e8f0',
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    gap: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    width: '100%',
  },
  sectionTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'right',
    color: FOREGROUND,
    flex: 1,
    lineHeight: 20,
  },
  addBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    minHeight: 32,
  },
  addBtnText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'center',
    color: FOREGROUND,
  },
  cardsRow: {
    flexDirection: 'row-reverse',
    gap: 20,
    borderRadius: 8,
    padding: 12,
    backgroundColor: '#f1f5f9',
  },
  cardSpacer: {
    flex: 1,
  },
});

/* ── Specialization Card ────────────────────────────────────── */
const specCardStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderColor: '#e2e8f0',
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    gap: 12,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    justifyContent: 'flex-end',
  },
  nameCol: {
    flex: 1,
    alignItems: 'flex-end',
  },
  name: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
    textAlign: 'right',
  },
  statsRow: {
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'flex-end',
  },
  statItem: {
    alignItems: 'center',
    gap: 4,
    minWidth: 60,
  },
  statLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'center',
  },
  statValue: {
    fontSize: 14,
    fontFamily: FONTS.numbers,
    fontWeight: '700',
    color: FOREGROUND,
    textAlign: 'center',
  },
  footerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  linkText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
  },
  dropdownMenu: {
    position: 'absolute' as const,
    bottom: 24,
    left: 0,
    zIndex: 10,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    minWidth: 160,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 6,
  },
  dropdownItem: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'flex-end' as const,
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  dropdownItemText: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#DC2626',
  },
});

/* ── Program / Activity Card ────────────────────────────────── */
const progCardStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderColor: '#e2e8f0',
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    gap: 16,
  },
  title: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'right',
    color: FOREGROUND,
    lineHeight: 20,
  },
  progressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  pctPill: {
    backgroundColor: '#f1f5f9',
    borderRadius: 100,
    paddingHorizontal: 8,
    paddingVertical: 2,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
  },
  pctText: {
    fontSize: 14,
    fontFamily: FONTS.numbers,
    fontWeight: '600',
    color: FOREGROUND,
    textAlign: 'center',
  },
  trackBg: {
    flex: 1,
    height: 8,
    borderRadius: 8,
    backgroundColor: '#f1f5f9',
    overflow: 'visible',
    flexDirection: 'row-reverse',
    alignItems: 'center',
  },
  trackFill: {
    height: 8,
    borderRadius: 8,
    backgroundColor: '#2ca02c',
  },
  progressDot: {
    position: 'absolute',
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: '#2ca02c',
    borderWidth: 2,
    borderColor: '#FFFFFF',
    transform: [{ translateX: 7 }],
  },
  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    justifyContent: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
    minHeight: 24,
  },
  linkText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    textAlign: 'right',
    color: FOREGROUND,
    lineHeight: 14,
  },
});

/* ── Output Card ────────────────────────────────────────────── */

/* ── Confirmation Modal ────────────────────────────────────── */
const modalStyles = StyleSheet.create({
  overlay: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center', alignItems: 'center',
  },
  content: {
    backgroundColor: '#FFFFFF', borderRadius: 16, padding: 24,
    width: 400, maxWidth: '90%',
  },
  title: {
    fontSize: 18, fontFamily: FONTS.medium, color: FOREGROUND,
    textAlign: 'center', marginBottom: 8,
  },
  message: {
    fontSize: 14, fontFamily: FONTS.regular, color: '#64748B',
    textAlign: 'center', lineHeight: 22, marginBottom: 20,
  },
  btnRow: { flexDirection: 'row', gap: 12, justifyContent: 'center' },
  cancelBtn: {
    flex: 1, borderWidth: 1, borderColor: '#CBD5E1', borderRadius: 8,
    paddingVertical: 10, alignItems: 'center',
  },
  cancelBtnText: { fontSize: 14, fontFamily: FONTS.medium, color: FOREGROUND },
  confirmBtn: {
    flex: 1, backgroundColor: '#DC2626', borderRadius: 8,
    paddingVertical: 10, alignItems: 'center',
  },
  confirmBtnText: { fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' },
});
